import { useFlag } from "@hermes/flags";
import { Message, useMessage } from "@hermes/intl";
import {
  cssFns,
  truthy,
  usePrincipalColors,
  useScreenDetector,
} from "@hermes/ui";
import { NavLink, type To } from "react-router-dom";

import { useAppConfig } from "#internal/app/config/context";
import { useRoutePath } from "#internal/app/router";
import { StyledNavLink } from "#internal/shared/ui/styled-nav-link";

import arrowRightIcon from "../../shared/assets/images/arrow_right.svg";
import { AlternativeLinks } from "./alternative-links";
import { Copyright } from "./copyright";
import { FooterLink } from "./footer-link";

export const Footer = () => {
  const { routePath } = useRoutePath();
  const principalColors = usePrincipalColors();
  const { isMobile, isDesktop } = useScreenDetector();
  const message = useMessage();
  const { city, socialMediaLinks, country } = useAppConfig();

  const flag = useFlag();

  const enableImpresszum = flag({ id: "legals.impresszum" });
  const enablePrivacyPolicy = flag({ id: "legals.privacy_policy" });
  const enableUserGTC = flag({ id: "legals.user_gtc" });
  const enableProcessingAgreement = flag({ id: "legals.processing_agreement" });
  const enablePartnerGTC = flag({ id: "legals.partner_gtc" });

  const isConnectClinicsEnabled = flag({
    id: "182ff80f-e11a-47e7-80c8-ac036bf562c3",
    description: "Enable connect clinics link in footer",
  });
  const footerLinks: FooterLinksType[] = [
    isConnectClinicsEnabled && {
      link: routePath({ path: "/join" }),
      name: message({
        id: "common.link.join-clinic",
      }),
      icon: arrowRightIcon,
    },
    enableImpresszum && {
      link: routePath({ path: "/impresszum" }),
      name: message({
        id: "common.link.gdpr.impresszum",
        default: "Impresszum",
      }),
    },
    enablePrivacyPolicy && {
      link: routePath({ path: "/privacy_policy" }),
      name: message({
        id: "common.link.gdpr.privacy-policy",
        default: "Data Protection Policy",
      }),
    },
    enableUserGTC && {
      link: routePath({ path: "/user_gtc" }),
      name: message({
        id: "common.link.gdpr.user-gtc",
        default: "User Agreement",
      }),
    },
    enablePartnerGTC && {
      link: routePath({ path: "/partner_gtc" }),
      name: message({
        id: "common.link.gdpr.partner-gtc",
        default: "Partner Agreement",
      }),
    },
    enableProcessingAgreement && {
      link: routePath({ path: "/processing-agreement" }),
      name: message({
        id: "common.link.processing-agreement",
        default: "Согласие на обработку персональных данных",
      }),
    },
    {
      link: routePath({ path: "/contacts" }),
      name: message({
        id: "contacts.main-title",
      }),
    },
  ].filter(truthy);

  return (
    <footer
      css={{
        backgroundColor: principalColors.ebblue,
        padding: isMobile ? "0 17px" : "30px 60px 20px ",
      }}
    >
      {!isMobile && (
        <div
          css={{
            marginBottom: 30,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <div
            css={{
              display: "flex",
              flexDirection: "column",
              gap: 15,
            }}
          >
            <NavLink to={routePath({ path: "/" })}>
              <img src="/logo.svg" alt="logo" />
            </NavLink>
            <p
              css={[
                cssFns.typo({ level: "body-3", weight: "regular" }),
                {
                  maxWidth: "285px",
                  margin: 0,
                  lineHeight: "20px",
                  color: principalColors.white,
                  textWrap: "balance",
                },
              ]}
            >
              <Message
                id="15582897-1267-4aa6-ae21-6de87110e5b7"
                default="{brand} – сервис бесплатного поиска врача и онлайн запись на прием"
                values={{
                  brand: country.brandName,
                }}
              />
            </p>
            {socialMediaLinks && (
              <div
                css={{
                  display: "flex",
                  gap: 20,
                  marginBottom: "15px",
                }}
              >
                {socialMediaLinks.facebook && (
                  <a href={socialMediaLinks.facebook} target="_blank">
                    {facebookIcon}
                  </a>
                )}
                {socialMediaLinks.instagram && (
                  <a href={socialMediaLinks.instagram} target="_blank">
                    {instagramIcon}
                  </a>
                )}
              </div>
            )}
          </div>
          <nav
            css={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
              paddingTop: "9px",
            }}
          >
            <div
              css={[
                { color: principalColors.white, marginBottom: "5px" },
                cssFns.typo({ level: "body-1", weight: "semibold" }),
              ]}
            >
              <Message
                id="2d77c544-0459-4c8d-95ee-93c43c98287a"
                default="Пациентам"
              />
            </div>
            <StyledNavLink
              to={routePath({
                path: `/clinics/:city`,
                params: { city: city.slug },
              })}
            >
              <Message id="common.clinics" />
              &nbsp;
              {city.name}
            </StyledNavLink>
            <StyledNavLink
              to={routePath({
                path: `/procedures/:city`,
                params: { city: city.slug },
              })}
            >
              <Message id="common.services.procedures" />
              &nbsp;
              {city.name}
            </StyledNavLink>
          </nav>
          <nav
            css={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
              paddingTop: "9px",
            }}
          >
            <div
              css={[
                { color: principalColors.white, marginBottom: "5px" },
                cssFns.typo({ level: "body-1", weight: "semibold" }),
              ]}
            >
              <Message
                id="2d80a77d-e2c4-4dfb-9066-e4fb30cb7d54"
                default="Клиникам"
              />
            </div>
            {import.meta.env.DOQ_CABINET_URL && (
              <StyledNavLink to={import.meta.env.DOQ_CABINET_URL}>
                <Message
                  id="3b38f527-6b01-4bc1-b149-4e11e6b32913"
                  default="Личный кабинет"
                />
              </StyledNavLink>
            )}
            {isConnectClinicsEnabled && (
              <StyledNavLink to={routePath({ path: "/join" })}>
                <Message id="common.link.join-clinic" />
              </StyledNavLink>
            )}
          </nav>
          <nav
            css={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
              paddingTop: "9px",
            }}
          >
            <div
              css={[
                { color: principalColors.white, marginBottom: "5px" },
                cssFns.typo({ level: "body-1", weight: "semibold" }),
              ]}
            >
              <Message
                id="ac1a120b-4c1f-4210-a9b7-c26e435aa384"
                default="Интернет-сервис"
              />
            </div>
            <StyledNavLink
              to={routePath({
                path: `/contacts`,
              })}
            >
              {message({
                id: "contacts.main-title",
              })}
            </StyledNavLink>
          </nav>
        </div>
      )}
      {isMobile && (
        <nav
          css={{
            display: "flex",
            flexDirection: "column",
            gap: 24,
            padding: "30px 0",
          }}
        >
          {footerLinks.map(({ name, icon, link }, index) => (
            <FooterLink name={name} to={link} icon={icon} key={index} />
          ))}
        </nav>
      )}
      <aside
        css={[
          cssFns.borderBlockStart({
            width: 1,
            style: "solid",
            color: "rgba(231, 240, 246, 0.3)",
          }),
          {
            display: "flex",
            flexDirection: "column",
            gap: 30,
            padding: isMobile ? "30px 0 20px" : "16px 0 0",
            overflowX: "scroll",
          },
        ]}
      >
        <AlternativeLinks />
        <div css={{ display: "flex", justifyContent: "space-between" }}>
          <Copyright />
          {isDesktop && (
            <nav
              css={{
                display: "flex",
                gap: 20,
                flexWrap: "nowrap",
                justifyContent: "flex-end",
              }}
            >
              {enableImpresszum && (
                <StyledNavLink
                  css={{ opacity: 0.5 }}
                  to={routePath({ path: "/impresszum" })}
                >
                  <Message id="common.link.gdpr.impresszum" />
                </StyledNavLink>
              )}
              {enablePrivacyPolicy && (
                <StyledNavLink
                  css={{ opacity: 0.5 }}
                  to={routePath({ path: "/privacy_policy" })}
                >
                  <Message id="common.link.gdpr.privacy-policy" />
                </StyledNavLink>
              )}
              {enableUserGTC && (
                <StyledNavLink
                  css={{ opacity: 0.5 }}
                  to={routePath({ path: "/user_gtc" })}
                >
                  <Message id="common.link.gdpr.user-gtc" />
                </StyledNavLink>
              )}
              {enablePartnerGTC && (
                <StyledNavLink
                  to={routePath({ path: "/partner_gtc" })}
                  css={{ opacity: 0.5 }}
                >
                  <Message id="common.link.gdpr.partner-gtc" />
                </StyledNavLink>
              )}
              {enableProcessingAgreement && (
                <StyledNavLink
                  css={{ opacity: 0.5 }}
                  to={routePath({ path: "/processing-agreement" })}
                >
                  <Message id="common.link.processing-agreement" />
                </StyledNavLink>
              )}
            </nav>
          )}
        </div>
      </aside>
    </footer>
  );
};

type FooterLinksType = {
  link: To;
  name: string;
  icon?: string;
};

const facebookIcon = (
  <svg
    width="30"
    height="30"
    viewBox="0 0 36 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle
      cx="18"
      cy="18"
      r="17.25"
      fill="#5194C1"
      stroke="white"
      strokeWidth="1.5"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M18.9461 26.8801H15.115V18.239H13.2007V15.2616H15.115V13.474C15.115 11.0452 16.1954 9.6001 19.2668 9.6001H21.8231V12.5786H20.2254C19.0297 12.5786 18.9507 12.9947 18.9507 13.7712L18.9455 15.2616H21.8407L21.5018 18.239H18.9455L18.9461 26.8801Z"
      stroke="white"
      strokeWidth="1.5"
    />
  </svg>
);

const instagramIcon = (
  <svg
    width="30"
    height="30"
    viewBox="0 0 36 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle
      cx="18"
      cy="18"
      r="17.25"
      fill="#5194C1"
      stroke="white"
      strokeWidth="1.5"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M18.0022 11.4401C20.2852 11.4401 20.5556 11.4488 21.4572 11.49C22.2908 11.528 22.7435 11.6673 23.0448 11.7844C23.4439 11.9395 23.7287 12.1248 24.0279 12.4239C24.3271 12.7231 24.5124 13.008 24.6675 13.407C24.7845 13.7083 24.9238 14.1611 24.9619 14.9947C25.003 15.8963 25.0117 16.1667 25.0117 18.4496C25.0117 20.7326 25.003 21.003 24.9619 21.9046C24.9238 22.7382 24.7845 23.1909 24.6675 23.4922C24.5124 23.8913 24.3271 24.1761 24.0279 24.4753C23.7287 24.7745 23.4439 24.9598 23.0448 25.1149C22.7435 25.232 22.2908 25.3712 21.4572 25.4093C20.5557 25.4504 20.2853 25.4591 18.0022 25.4591C15.7191 25.4591 15.4487 25.4504 14.5473 25.4093C13.7137 25.3712 13.2609 25.232 12.9597 25.1149C12.5606 24.9598 12.2757 24.7745 11.9765 24.4753C11.6774 24.1761 11.492 23.8913 11.337 23.4922C11.2199 23.1909 11.0806 22.7382 11.0425 21.9046C11.0014 21.003 10.9927 20.7326 10.9927 18.4496C10.9927 16.1667 11.0014 15.8963 11.0425 14.9947C11.0806 14.1611 11.2199 13.7083 11.337 13.407C11.492 13.008 11.6774 12.7231 11.9765 12.4239C12.2757 12.1248 12.5606 11.9395 12.9597 11.7844C13.2609 11.6673 13.7137 11.528 14.5472 11.49C15.4488 11.4488 15.7192 11.4401 18.0022 11.4401ZM18.0007 9.89941C15.6786 9.89941 15.3875 9.90926 14.4756 9.95087C13.5655 9.99241 12.944 10.1369 12.4001 10.3483C11.8379 10.5668 11.361 10.8592 10.8857 11.3345C10.4104 11.8098 10.118 12.2866 9.89953 12.8489C9.68816 13.3927 9.54364 14.0142 9.5021 14.9243C9.46049 15.8362 9.45068 16.1274 9.45068 18.4495C9.45068 20.7716 9.46049 21.0627 9.5021 21.9746C9.54364 22.8847 9.68816 23.5062 9.89953 24.0501C10.118 24.6123 10.4104 25.0892 10.8857 25.5645C11.361 26.0398 11.8379 26.3322 12.4001 26.5507C12.944 26.762 13.5655 26.9066 14.4756 26.9481C15.3875 26.9897 15.6786 26.9995 18.0007 26.9995C20.3228 26.9995 20.614 26.9897 21.5259 26.9481C22.436 26.9066 23.0575 26.762 23.6013 26.5507C24.1636 26.3322 24.6404 26.0398 25.1157 25.5645C25.5911 25.0892 25.8834 24.6124 26.1019 24.0501C26.3133 23.5062 26.4578 22.8847 26.4993 21.9746C26.541 21.0627 26.5508 20.7716 26.5508 18.4495C26.5508 16.1274 26.541 15.8362 26.4993 14.9243C26.4578 14.0142 26.3133 13.3927 26.1019 12.8489C25.8834 12.2866 25.5911 11.8098 25.1157 11.3345C24.6404 10.8592 24.1636 10.5668 23.6013 10.3483C23.0575 10.1369 22.436 9.99241 21.5259 9.95087C20.614 9.90926 20.3228 9.89941 18.0007 9.89941ZM18.0189 14.0733C15.5923 14.0733 13.6252 16.0405 13.6252 18.4671C13.6252 20.8937 15.5923 22.8609 18.0189 22.8609C20.4455 22.8609 22.4127 20.8937 22.4127 18.4671C22.4127 16.0405 20.4455 14.0733 18.0189 14.0733ZM18.0166 21.3192C16.4414 21.3192 15.1645 20.0422 15.1645 18.4671C15.1645 16.8919 16.4414 15.6149 18.0166 15.6149C19.5917 15.6149 20.8687 16.8919 20.8687 18.4671C20.8687 20.0422 19.5917 21.3192 18.0166 21.3192ZM23.5652 13.8704C23.5652 14.4325 23.1095 14.8883 22.5474 14.8883C21.9852 14.8883 21.5295 14.4325 21.5295 13.8704C21.5295 13.3082 21.9852 12.8525 22.5474 12.8525C23.1095 12.8525 23.5652 13.3082 23.5652 13.8704Z"
      fill="white"
    />
  </svg>
);
